import { notification } from "antd";
import { isEmpty, isNil, uniq } from "lodash";
import { useEffect, useRef } from "react";
import useLucie, {
  LUCIE_OUTCOMES,
  mergeProposals,
  readCachedProposals,
  storeProposals,
  zonesWithoutProposal,
} from "./use-lucie";

const useLucieProposals = ({ template: { id }, content, update, t }) => {
  const lucie = useLucie({ template: { id } });
  const askedRef = useRef(new Set());
  const askRef = useRef(null);

  const ask = async (targets, { silent = false } = {}) => {
    if (isEmpty(targets)) {
      if (!silent) notification.info({ message: t("LUCIE.ALL_SETTLED") });
      return;
    }
    const { outcome, proposals = [] } = await lucie.ask({ zones: targets });
    if (outcome === LUCIE_OUTCOMES.CANCELLED) return;
    if (
      outcome === LUCIE_OUTCOMES.FOUND ||
      outcome === LUCIE_OUTCOMES.NOTHING
    ) {
      storeProposals({ template: { id }, zones: targets, proposals });
      update((current) => mergeProposals(current, proposals, targets));
    }
    if (silent) return;
    if (outcome === LUCIE_OUTCOMES.FAILED) {
      notification.error({ message: t("LUCIE.FAILED") });
      return;
    }
    const targetIds = new Set(targets.map((zone) => zone.id));
    const found = uniq(proposals.map(({ zone }) => zone)).filter((zoneId) =>
      targetIds.has(zoneId),
    ).length;
    notification.info({
      message:
        found > 0 ? t("LUCIE.FOUND", { count: found }) : t("LUCIE.NOTHING"),
    });
  };
  askRef.current = ask;

  useEffect(() => {
    if (isNil(content)) return;
    const targets = zonesWithoutProposal(content);
    if (isEmpty(targets)) return;
    const { proposals: cached, covered } = readCachedProposals({
      template: { id },
      zones: targets,
    });
    if (!isEmpty(covered))
      update((current) => mergeProposals(current, cached, covered));
    const coveredIds = new Set(covered.map((zone) => zone.id));
    const fresh = targets.filter(
      (zone) => !coveredIds.has(zone.id) && !askedRef.current.has(zone.id),
    );
    if (isEmpty(fresh)) return;
    for (const zone of fresh) askedRef.current.add(zone.id);
    askRef.current(fresh, { silent: true });
  }, [content, id, update]);

  return { running: lucie.running, ask };
};

export default useLucieProposals;
